import React, { useContext, useState, useEffect, useRef } from 'react';
import { Step, StepLabel, Stepper, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import { useLocation } from 'react-router-dom';

import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { ProgressBarStep } from 'shared/components/ProgressBar/ProgressBarStep';
import { ProgressBarConnector } from 'shared/components/ProgressBar/ProgressBarConnector';


const StyledDiv = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 50,
  left: 15,
  zIndex: 999,
  transition: 'opacity 0.3s ease, transform 0.3s ease',
  backgroundColor: 'rgba(0,0,0,0.3)',
  backdropFilter: 'blur(8px)',
  borderRadius: 24,
  padding: '4px 0',

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

export const ProgressBar = () => {
  const [introComplete, setIntroComplete] = useState(false);
  const [visible, setVisible] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleComplete = () => {
      setIntroComplete(true);
      setVisible(true);
    };
    window.addEventListener('homeAnimationComplete', handleComplete);
    return () => window.removeEventListener('homeAnimationComplete', handleComplete);
  }, []);

  useEffect(() => {
    if (!introComplete) return;
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = Math.abs(currentY - lastScrollY.current);
      if (delta < 5) return;
      if (currentY < 100) {
        setVisible(true);
      } else if (currentY < lastScrollY.current) {
        setVisible(true);
      } else {
        setVisible(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [introComplete]);

  // Show stepper when mouse moves to left edge
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientX <= 40 && introComplete) {
        setVisible(true);
      }
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [introComplete]);

  const { pathname } = useLocation();
  const { menuItems } = useContext(LayoutContext);
  const activeIndex = menuItems.findIndex((item) => item.to === pathname);

  return (
    <StyledDiv style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(0)' : 'translateX(-20px)',
    }}>
      <Stepper
        nonLinear
        orientation="vertical"
        connector={<ProgressBarConnector/>}
      >
        {menuItems.map((menuItem, index) => {
          if(menuItem?.menuOnly) {
            return null;
          }
          return (
            <Step key={index}>
              <Tooltip title={<Typography variant="body1">{menuItem.name}</Typography>} placement="right">
                <StepLabel
                  StepIconComponent={ProgressBarStep}
                  StepIconProps={{
                    icon: index,
                    active: activeIndex === index,
                  }}
                />
              </Tooltip>
            </Step>
          )
        })}
      </Stepper>
    </StyledDiv>
  )
};
